import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IoClose, IoSettingsSharp, IoShuffle } from "react-icons/io5";

// MUI
import Button from "@mui/material/Button";

// Components
import Progress from '../../../../components/progress';

// Styles
import styles from './header.module.css';

// Hooks
import useDarkMode from '../../../../utils/hooks/darkMode'
import MenuButton from '../../../../components/MenuButton';

// Assets
import infiniteLives from '../../../../components/assets/heart_infinite.png'

// Types
interface HeaderProps {
  totalQuestions: number;
  currentQuestion: number;
  onQuit: () => void;
  onShuffle: () => void;
  onRegenerate: () => void;
  type: 'quiz' | 'practice';
  title?: string | null;
  timeLeft?: number | null;
  lives: number | 'infinite';
  fromLevel: boolean;
}

const Header: React.FC<HeaderProps> = ({ totalQuestions, currentQuestion, onQuit, onShuffle, onRegenerate, type, title, timeLeft, lives, fromLevel }) => {
  const { t } = useTranslation();

  const isDarkMode = useDarkMode();

  const handleMenuButton = useCallback((option: any) => {
    switch (option.value) {
      case 'regenerate':
        onRegenerate();
        break;
      case 'shuffle':
        onShuffle();
        break;
    }
  }, [onShuffle, onRegenerate]);

  // Render
  const memoizedOptions = useMemo(() => {
    return [
      { label: t('questionCards.actions.regenerate'), value: 'regenerate' },
      { label: t('questionCards.actions.shuffle'), value: 'shuffle' }
    ]
  }, [t]);

  const progressPercentage = (currentQuestion / totalQuestions) * 100;

  const renderLivesStatus = () => {
    if (lives === 0) return null;

    if (lives === 'infinite') {
      return (
        <div className={styles.livesContainer}>
          <img src={infiniteLives} alt='infinite lives' className='w-6 h-6' />
        </div>
      )
    }

    return (
      <div className={styles.livesContainer}>
        <span className='text-red-500'>
          ❤️
        </span>

        <span className='text-red-500 text-sm'>
          {lives}
        </span>
      </div>
    )
  }

  const renderTimeLeft = useCallback(() => {
    if (timeLeft === null || timeLeft === undefined) return null;

    const m = Math.floor(timeLeft / 60);
    const s = timeLeft % 60;

    return (
      <span className='text-lg text-black dark:text-white'>
        {`${m}:${s.toString().padStart(2, '0')}`}
      </span>
    )
  }, [timeLeft])

  const typeText = () => {
  }

  const renderTitle = useCallback(() => {
    if (!title) return null;

    let typeText = '';
    switch (type) {
      case 'quiz':
        typeText = t('questionCards.type.quiz');
        break;
      default:
        typeText = t('questionCards.type.default');
        break;
    }

    return (
      <div className={styles.titleContainer}>
        <h1 className={`${styles.titleIcon}`}>
          📚
        </h1>

        <div className='flex flex-col gap-1'>
          <p className='text-md text-gray-500 dark:text-gray-400'>
            {title}
          </p>

          <p className='text-sm text-gray-500 dark:text-gray-400'>
            {typeText}
          </p>
        </div>
      </div>
    )
  }, [title, type, t])

  const renderQuestionActions = useCallback(() => {
    return (
      <div className={styles.actionsContainer}>
        <div className='flex flex-row gap-2 items-center'>
          {renderTimeLeft()}

          <MenuButton
            options={memoizedOptions}
            onSelect={handleMenuButton}
            renderOption={renderAction}
            className={`dark:bg-stone-650 dark:text-white`}
          >
            {t('actions.actions')}
          </MenuButton>
        </div>

        <Button
          variant="contained"
          className={styles.quitButton}
          onClick={onQuit}
          color='secondary'
        >
          <IoClose />
          <span className='hidden md:block'> {t('actions.quit')} </span>
        </Button>
      </div >
    )
  }, [renderTimeLeft, memoizedOptions, handleMenuButton, onQuit, t])

  const renderAction = (option: any) => {
    let icon = null;
    switch (option.value) {
      case 'regenerate':
        icon = <IoSettingsSharp className='text-blue-500 w-4 h-4' />;
        break;
      case 'shuffle':
        icon = <IoShuffle className='text-blue-500 w-4 h-4' />;
        break;
    }

    return (
      <div className='flex flex-row gap-2 items-center'>
        {icon}

        <p className='text-sm text-blue-500 dark:text-blue-500'>
          {option.label}
        </p>
      </div>
    )
  }

  return (
    <div className={`${styles.container} ${fromLevel ? '' : 'mb-4 border-gray-200 border rounded-3xl dark:bg-stone-650 dark:border-gray-400"'}`}>
      {!!title && !fromLevel && (
        <div className={styles.topContainer}>
          {renderTitle()}
          {renderQuestionActions()}
        </div >
      )}

      <div className={`${styles.bottomContainer} ${fromLevel ? 'flex-col align-start' : 'flex-row align-center'}`}>
        {fromLevel && (
          <div className='flex flex-row justify-between w-full'>
            <p className='text-sm text-gray-500 dark:text-gray-400 flex flex-row'>
              <span className=''>Question {currentQuestion} of {totalQuestions}</span>
            </p>
            <p className='text-sm text-gray-500 dark:text-gray-400 flex flex-row'>{progressPercentage}%</p>
          </div>
        )}
        {!fromLevel && (
          <p className='text-sm text-gray-500 dark:text-gray-400 flex flex-row'>
            <span className='text-blue-500'>{currentQuestion}</span>
            /{totalQuestions}
          </p>
        )}
        <div className='flex flex-1 w-full'>
          <Progress progress={progressPercentage} />
        </div>

        {/* {renderLivesStatus()} */}

        {!title && renderQuestionActions()}
      </div>
    </div >
  )
}

export default Header;
